import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AdminLayout from "../components/AdminLayout";
import { listAdminProducts, TourStatus, type AdminProduct } from "@/lib/admin";
import { TourCategory } from "@/lib/tours";

const categoryKey: Record<TourCategory, string> = {
  [TourCategory.Adventure]: "admin.cat.adventure",
  [TourCategory.Cultural]: "admin.cat.cultural",
  [TourCategory.Gastronomic]: "admin.cat.gastronomic",
  [TourCategory.Transport]: "admin.cat.transport",
  [TourCategory.Housing]: "admin.cat.housing",
  [TourCategory.Fishing]: "admin.cat.fishing",
};

function statusBadge(status: TourStatus, t: (k: string) => string): { label: string; cls: string } {
  if (status === TourStatus.Active) return { label: t("admin.prod.statusActive"), cls: "bg-ocean/10 text-ocean" };
  if (status === TourStatus.Paused) return { label: t("admin.prod.statusPaused"), cls: "bg-sand/60 text-charcoal" };
  return { label: t("admin.prod.statusArchived"), cls: "bg-gray-100 text-gray-500" };
}

export default function AdminProductosPage() {
  const { t } = useTranslation("admin");
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listAdminProducts({ search: search.trim() || undefined })
      .then((data) => { if (!cancelled) setProducts(data); })
      .catch(() => { if (!cancelled) setProducts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [search]);

  return (
    <AdminLayout title={t("admin.prod.title")}>
      <div className="mb-4">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("admin.prod.search")}
          className="w-full sm:w-80 border border-gray-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-ocean"
        />
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.prod.tour")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.prod.provider")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.prod.category")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.prod.price")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.prod.status")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3">{t("admin.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="py-10 text-center text-gray-400">{t("admin.loading")}</td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan={6} className="py-10 text-center text-gray-400">{t("admin.prod.empty")}</td></tr>
            ) : (
              products.map((p) => {
                const badge = statusBadge(p.status, t);
                return (
                  <tr key={p.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-4 font-medium text-charcoal">{p.title}</td>
                    <td className="py-3 pr-4 text-gray-600">{p.providerName}</td>
                    <td className="py-3 pr-4 text-gray-600">{categoryKey[p.category] ? t(categoryKey[p.category]) : "—"}</td>
                    <td className="py-3 pr-4 font-medium text-charcoal">${p.priceAdult.toLocaleString("es-MX")}</td>
                    <td className="py-3 pr-4">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${badge.cls}`}>{badge.label}</span>
                    </td>
                    <td className="py-3">
                      <Link to={`/tours/${p.slug}`} className="text-xs text-ocean hover:underline">{t("admin.view")}</Link>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
